import { get, map } from 'lodash'
import dynamic from 'next/dynamic'
import React, { useState } from 'react'
import { CUSTOM_CONTENT } from 'shared-constants'
import styled from 'styled-components'
import { Box, Flex } from 'components/Layout'
import { MarkdownRenderer } from 'components/MarkdownRenderer'
import { QA } from 'components/QA'
import { H5, Text } from 'components/Typography'
import { GA4_EVENTS, useGoogleAnalytics } from 'providers/googleAnalytics'
import { COLOR_INTENT, FONT_SIZE, FONT_STACK, FONT_WEIGHT, SPACE } from 'Theme'
import { type PublicProductQuery } from 'types/graphql-generated'
import { formatDimensionsText } from '../utils'

const Proposition65WarningModal = dynamic(
  () =>
    import('./Proposition65WarningModal').then(
      (module) => module.Proposition65WarningModal
    ),
  { ssr: false }
)

const ACCORDION_ITEMS = {
  DETAIL: {
    title: 'Details & Dimensions',
  },
  SHIPPING: {
    title: 'Shipping & Returns',
  },
  CARE: {
    title: 'Care',
  },
}

const TextButton = styled(Text)`
  &:hover {
    cursor: pointer;
  }
`

const AccordionContent = styled('div')`
  font-size: ${FONT_SIZE.PX_16};
  font-family: ${FONT_STACK.SERIF};
`
interface ProductAccordionProps {
  selectedVariant: PublicProductQuery['product']['variants'][0]
}

export const ProductAccordion = React.forwardRef<
  HTMLDivElement,
  ProductAccordionProps
>(({ selectedVariant }, ref) => {
  const [isProposition65ModalOpen, setIsProposition65ModalOpen] =
    useState(false)
  const { executeDataToDataLayer } = useGoogleAnalytics()
  const customContent = get(selectedVariant, 'customContent', {})
  const customDimensions = get(customContent, CUSTOM_CONTENT.CUSTOM_DIMENSIONS)
  const proposition65 = get(
    selectedVariant,
    ['managedCustomFields', 'proposition65'],
    ''
  )
  const isProp65Eligible = get(
    selectedVariant,
    ['managedCustomFields', 'prop65Eligible'],
    false
  )

  const accordionValues = {
    DETAIL: {
      customContent: get(customContent, CUSTOM_CONTENT.ACCORDION_DETAILS, null),
      otherContent: (
        <>
          <Flex my={SPACE.PX_15} flexDirection="column">
            {formatDimensionsText(selectedVariant).map(({ key, value }) => (
              <Text
                key={key}
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontSize={FONT_SIZE.PX_16}
                fontFamily={FONT_STACK.SERIF}
              >
                {`${key}: `}
                <Text fontSize={FONT_SIZE.PX_16} fontFamily={FONT_STACK.SERIF}>
                  {value}
                </Text>
              </Text>
            ))}
            {customDimensions && (
              <Text
                fontWeight={FONT_WEIGHT.MEDIUM}
                fontSize={FONT_SIZE.PX_16}
                fontFamily={FONT_STACK.SERIF}
              >
                Other:{' '}
                <Text fontSize={FONT_SIZE.PX_16} fontFamily={FONT_STACK.SERIF}>
                  {customDimensions}
                </Text>
              </Text>
            )}
            {isProp65Eligible && (
              <TextButton
                color={COLOR_INTENT.PRODUCT_ACCORDION.PROPOSITION_65_BUTTON}
                onClick={() => setIsProposition65ModalOpen(true)}
                mt={SPACE.PX_20}
                fontSize={FONT_SIZE.PX_16}
              >
                WARNING For California Residents
              </TextButton>
            )}
          </Flex>
          {isProp65Eligible && (
            <Proposition65WarningModal
              isOpen={isProposition65ModalOpen}
              onModalClose={() => setIsProposition65ModalOpen(false)}
              chemical={proposition65}
            />
          )}
        </>
      ),
      initialOpenState: true,
    },
    SHIPPING: {
      customContent: get(
        customContent,
        CUSTOM_CONTENT.ACCORDION_SHIPPING,
        null
      ),
    },
    CARE: {
      customContent: get(customContent, CUSTOM_CONTENT.ACCORDION_CARE, null),
    },
  }

  return (
    <Box ref={ref}>
      {map(ACCORDION_ITEMS, ({ title }, key) => {
        const {
          customContent: customContentValue,
          otherContent: otherContentValue,
          initialOpenState,
        } = accordionValues[key]

        if (customContentValue || otherContentValue) {
          return (
            <QA
              key={key}
              title={title}
              headingComp={H5}
              initialOpenState={initialOpenState}
              onClick={() =>
                executeDataToDataLayer({
                  event: GA4_EVENTS.CLICK,
                  overrideData: {
                    text: title,
                    name: `${title} option from product details accordion`,
                  },
                })
              }
            >
              {customContentValue && (
                <MarkdownRenderer
                  markdownSource={customContentValue}
                  fontSize={FONT_SIZE.PX_16}
                />
              )}
              {otherContentValue && (
                <AccordionContent>{otherContentValue}</AccordionContent>
              )}
            </QA>
          )
        }
        return null
      })}
    </Box>
  )
})
